import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import trxReportDetails from "../services/TrxReportDetails";
import "react-datepicker/dist/react-datepicker.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "./TransactionReports.css";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSortDown, faSortUp } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";


const Pagination = ({ totalItems, itemsPerPage, currentPage, onPageChange }) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const pageNumbers = [];

  if (totalPages <= 5) {
    for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
  } else {
    if (currentPage <= 3) pageNumbers.push(1, 2, 3, "...", totalPages);
    else if (currentPage >= totalPages - 2)
      pageNumbers.push(1, "...", totalPages - 2, totalPages - 1, totalPages);
    else
      pageNumbers.push(
        1,
        "...",
        currentPage - 1,
        currentPage,
        currentPage + 1,
        "...",
        totalPages
      );
  }

  return (
    <nav aria-label="Page navigation">
      <ul className="pagination" style={{ justifyContent: "flex-end", marginRight: "50px" }}>
        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
          <button className="page-link" onClick={() => onPageChange(currentPage - 1)}>
            {useTranslation().t("<")}
          </button>
        </li>
        {pageNumbers.map((number, index) => (
          <li
            key={index}
            className={`page-item ${currentPage === number ? "active" : ""} ${
              number === "..." ? "disabled" : ""
            }`}
          >
            {number === "..." ? (
              <span className="page-link">...</span>
            ) : (
              <button className="page-link" onClick={() => onPageChange(number)}>
                {number}
              </button>
            )}
          </li>
        ))}
        <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
          <button className="page-link" onClick={() => onPageChange(currentPage + 1)}>
            {useTranslation().t(">")}
          </button>
        </li>
      </ul>
    </nav>
  );
};

const TransactionReports = ({ srvId, language }) => {
  const { t } = useTranslation();

  const [selectedDateRange, setSelectedDateRange] = useState([null, null]); // default null = all transactions
  const [serviceName, setServiceName] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  const [filteredData, setFilteredData] = useState([]);
  const [transactionData, setTransactionData] = useState([]);
  const [sortOrder, setSortOrder] = useState(null); // asc or desc
  const [services, setServices] = useState([]);
  const [plans, setPlans] = useState([]);
  const [selectedServiceId, setSelectedServiceId] = useState("");

  // Fetch transaction data on mount
  useEffect(() => {
    const fetchTransactionData = async () => {
      try {
        const serviceIdToUse = srvId || "";
        const planToUse = "";
        const response = await trxReportDetails.trxReportDetails(serviceIdToUse, planToUse, language);

        setTransactionData(response);
        setFilteredData(response); // default: all transactions

        // Extract unique services
        const uniqueServices = [
          ...new Map(response.map(item => [item.serviceId, { serviceId: item.serviceId, serviceName: item.serviceName }])).values(),
        ];
        setServices(uniqueServices);

        // Extract unique plans
        const uniquePlans = [
          ...new Map(response.map(item => [item.planId, { planId: item.planId, planName: item.planName }])).values(),
        ];
        setPlans(uniquePlans);
      } catch (error) {
        console.error("Failed to fetch transaction data:", error);
      }
    };

    fetchTransactionData();
  }, [language, srvId]);

  const handleDateRangeChange = dates => {
    setSelectedDateRange(dates);
  };

const handleViewReport = () => {
  const start = selectedDateRange ? selectedDateRange[0] : null;
  const end = selectedDateRange ? selectedDateRange[1] : null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Validate dates
  if (start && end && start > end) {
    Swal.fire("Error", t("start_date_after_end_date"), "error");
    return;
  }

  if (start && start > today) {
    Swal.fire("Error", t("start_date_future"), "error");
    return;
  }

  if (end && end > today) {
    Swal.fire("Error", t("end_date_future"), "error");
    return;
  }

  // Filter data
  const result = transactionData.filter((txn) => {
    const txnDate = new Date(txn.transactionDate);
    const serviceMatch = !serviceName || txn.serviceName === serviceName;
    const planMatch = !selectedPlan || txn.planId === selectedPlan;
    const dateStartMatch = !start || txnDate >= start;
    const dateEndMatch = !end || txnDate <= end;

    return serviceMatch && planMatch && dateStartMatch && dateEndMatch;
  });

  // Sort if needed
  if (sortOrder) {
    result.sort((a, b) => {
      const dateA = new Date(a.transactionDate);
      const dateB = new Date(b.transactionDate);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });
  }

  // Show alert if no data found
  if (result.length === 0) {
    Swal.fire({
      icon: "warning",
      title: "No Data Found",
      text: t("no_data_found"),
      confirmButtonText: "OK",
    });
  }

  setFilteredData(result);
  setCurrentPage(1);
};


  const handleSortByDate = () => {
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    handleViewReport();
  };

  const handleServiceNameChange = async e => {
    const selectedService = services.find(s => s.serviceName === e.target.value);
    setServiceName(selectedService?.serviceName || "");
    setSelectedServiceId(selectedService?.serviceId || "");
    setSelectedPlan("");

    if (!selectedService?.serviceId) {
      setPlans([]);
      return;
    }

    try {
      const response = await trxReportDetails.trxReportDetails(selectedService.serviceId, "");
      setFilteredData(response);

      const uniquePlans = [
        ...new Map(response.map(item => [item.planId, { planId: item.planId, planName: item.planName }])).values(),
      ];
      setPlans(uniquePlans);
    } catch (error) {
      console.error("Failed to fetch plans for selected service:", error);
    }
  };

  const handlePlanChange = e => {
    setSelectedPlan(e.target.value);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = pageNumber => setCurrentPage(pageNumber);
  const formatNumber = num => (num !== null && num !== undefined ? Number(num).toFixed(2) : "-");

  const exportToExcel = () => {
  if (filteredData.length === 0) {
    Swal.fire({
      icon: "warning",
      title: t("no_data_export"),
      confirmButtonColor: "#3085d6",
      confirmButtonText: "OK",
    });
    return;
  }
    const exportData = filteredData.map(row => ({
      [t("transaction_date")]: row.transactionDate,
      [t("transaction_id")]: row.transactionId,
      [t("service_name")]: row.serviceName,
      [t("plan")]: row.planName,
      [t("gst")]: row.gstPercentage,
      [t("amount")]: formatNumber(row.amount),
      [t("total_debit")]: formatNumber(row.totalDebit),
      [t("closing_balance")]: formatNumber(row.closingBalance),
      [t("auto_debit")]: row.isAutoDebit,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Transaction Report");
    XLSX.writeFile(workbook, "transaction_report.xlsx");
  };

  const exportToPdfEnglish = () => {
  if (filteredData.length === 0) {
    Swal.fire({
      icon: "warning",
      title: t("no_data_export"),
      text: t("no_data_export_message"),
      confirmButtonColor: "#3085d6",
      confirmButtonText: "OK",
    });
    return;
  }
    const doc = new jsPDF("l", "pt", "a4");
    const img = new Image();
    img.src = require("../assets/ms.png");
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.addImage(img, "PNG", 25, 30, 120, 20);

    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    const title = "Transaction Report";
    const textWidth = doc.getTextWidth(title);
    const textX = (pageWidth - textWidth) / 2;
    doc.text(title, textX, 45);

    doc.setDrawColor(0);
    doc.setLineWidth(1);
    doc.line(30, 60, pageWidth - 30, 60);

    const head = [
      [
        "Transaction Date",
        "Transaction Id",
        "Service Name",
        "Plan",
        "GST %",
        "Amount",
        "Total Debit",
        "Closing Balance",
        "Auto Debit",
      ],
    ];

    const body = filteredData.map(row => [
      row.transactionDate || "",
      row.transactionId || "",
      row.serviceName || "",
      row.planName || "",
      row.gstPercentage || "",
      row.amount !== null ? Number(row.amount).toFixed(2) : "",
      row.totalDebit !== null ? Number(row.totalDebit).toFixed(2) : "",
      row.closingBalance !== null ? Number(row.closingBalance).toFixed(2) : "",
      row.isAutoDebit || "",
    ]);

    doc.autoTable({
      head,
      body,
      startY: 70,
      styles: { fontSize: 9, cellPadding: 5, halign: "center", valign: "middle" },
      headStyles: { fillColor: [41, 128, 185], textColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      theme: "grid",
    });

    doc.save("transaction_report.pdf");
  };

  return (
    <div className="row p-1 rounded transaction-report-container">
      <h1 className="h3 mb-3" style={{ marginTop: "10px" }}>
        {t("transaction_reports")}
      </h1>

      <section>
        <div className="row" style={{ gap: "50px" }}>
          {/* Filters */}
          <div className="col-md-8 bg-secondary bg-opacity-25 rounded p-3 h-100">
            <div className="row g-3 align-items-end">
              {/* Date Range */}
              <div className="col-md-4 date-range-filter">
                <label htmlFor="dateRange" className="form-label" style={{ width: "110px", marginTop: "0px" }}>
                  {t("date_range")}
                </label>
                <DatePicker
                  id="dateRange"
                  selectsRange
                  startDate={selectedDateRange[0]}
                  endDate={selectedDateRange[1]}
                  onChange={handleDateRangeChange}
                  isClearable
                  dateFormat="yyyy-MM-dd"
                  maxDate={new Date()}
                  placeholderText={t("select_date_range")}
                  className="form-control form-control-sm custom-datepicker-input"
                />
              </div>

              {/* Service Dropdown */}
              <div className="col-md-4" style={{ paddingLeft: "30px" }}>
                <label htmlFor="serviceName" className="date-start-reportpage">
                  {t("service_name")}
                </label>
                <select
                  className="form-select form-select-sm"
                  id="serviceName"
                  value={serviceName}
                  onChange={handleServiceNameChange}
                  style={{ width: "200px", height: "48px", borderRadius: "5px", color: "grey", paddingLeft: "10px", fontSize: "12px" }}
                >
                  <option value="">{t("all_services")}</option>
                  {services.map(s => (
                    <option key={s.serviceId} value={s.serviceName}>
                      {s.serviceName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Plan Dropdown */}
              <div className="col-md-4" style={{ paddingLeft: "30px" }}>
                <label htmlFor="planName" className="date-start-reportpage">
                  {t("plan_name")}
                </label>
                <select
                  className="form-select form-select-sm"
                  id="planName"
                  value={selectedPlan}
                  onChange={handlePlanChange}
                  style={{ width: "200px", height: "48px", borderRadius: "5px", color: "grey", paddingLeft: "10px", fontSize: "12px" }}
                >
                  <option value="">{t("all_plans")}</option>
                  {plans.map(plan => (
                    <option key={plan.planId} value={plan.planId}>
                      {plan.planName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div
            className="col-md-4 d-flex flex-column align-items-stretch"
            style={{ gap: "8px", width: "300px" }}
          >
            <button
              type="button"
              className="btn btn-primary btn-sm"
              style={{ fontWeight: "bold", height: "45px" }}
              onClick={handleViewReport}
            >
              {t("view_report")}
            </button>

            <div className="d-flex" style={{ gap: "10px" }}>
              {/* Excel Button */}
              <button
                className="export-btn flex-grow-1"
                onClick={exportToExcel}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "5px",
                  padding: "10px 15px",
                  border: "1px solid green",
                  borderRadius: "5px",
                  backgroundColor: "#f0f9f0",
                  color: "green",
                  fontSize: "14px",
                  fontWeight: "bold",
                  cursor: "pointer",
                  transition: "background-color 0.3s ease, color 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "green";
                  e.target.style.color = "white";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#f0f9f0";
                  e.target.style.color = "green";
                }}
              >
                {t("export_to_excel")}
                <i className="fas fa-file-excel" style={{ fontSize: "18px" }}></i>
              </button>

              {/* PDF Button */}
              <button
                className="export-btn flex-grow-1"
                onClick={exportToPdfEnglish}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "5px",
                  padding: "10px 15px",
                  border: "1px solid red",
                  borderRadius: "5px",
                  backgroundColor: "#fff0f0",
                  color: "red",
                  fontSize: "14px",
                  fontWeight: "bold",
                  cursor: "pointer",
                  transition: "background-color 0.3s ease, color 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "red";
                  e.target.style.color = "white";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#fff0f0";
                  e.target.style.color = "red";
                }}
              >
                {t("export_to_pdf")}
                <i className="fas fa-file-pdf" style={{ fontSize: "18px" }}></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Table */}
      {filteredData.length > 0 && (
        <section>
          <p style={{ fontSize: "20px", marginTop: "10px", color: "#333333", fontWeight: "bold" }}>
            {t("transaction_summary")}
            &nbsp;
            <span className="fw-semibolds text-success">
              {selectedDateRange[0]?.toLocaleDateString("en-CA") || t("all_dates")} to {selectedDateRange[1]?.toLocaleDateString("en-CA") || t("all_dates")}
            </span>
            &nbsp;
            {t("for_service")}
            <span className="fw-semibold text-primary">&nbsp;{serviceName || t("all_services")}</span>
          </p>

          <div className="reports-table-wrapper">
            <table className="reports-table">
              <thead>
                <tr style={{ fontSize: "16px" }}>
                  <th>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {t("transaction_date")}
                      <div style={{ marginLeft: "5px", cursor: "pointer" }} onClick={handleSortByDate}>
                        {sortOrder === "asc" ? <FontAwesomeIcon icon={faSortUp} /> : <FontAwesomeIcon icon={faSortDown} />}
                      </div>
                    </div>
                  </th>
                  <th>{t("transaction_id")}</th>
                  <th>{t("service_name")}</th>
                  <th>{t("plan")}</th>
                  <th>{t("gst")}</th>
                  <th>{t("amount")}</th>
                  <th>{t("total_debit")}</th>
                  <th>{t("closing_balance")}</th>
                  <th>{t("auto_debit")}</th>
                </tr>
              </thead>
              <tbody className="small">
                {currentItems.map((row, idx) => (
                  <tr key={idx} style={{ fontSize: "16px" }}>
                    <td data-label={t("transaction_date")}>{row.transactionDate}</td>
                    <td data-label={t("transaction_id")}>{row.transactionId}</td>
                    <td data-label={t("service_name")}><strong>{row.serviceName}</strong></td>
                    <td data-label={t("plan")}>{row.planName}</td>
                    <td data-label={t("gst")}>{row.gstPercentage}</td>
                    <td data-label={t("amount")}>
                      ₹ {formatNumber(row.amount)}
                    </td>
                    <td data-label={t("total_debit")}>
                      ₹ {formatNumber(row.totalDebit)}
                    </td>
                    <td data-label={t("closing_balance")}>
                      ₹ {formatNumber(row.closingBalance)}
                    </td>
                    <td data-label={t("auto_debit")}>{row.isAutoDebit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredData.length > itemsPerPage && (
            <Pagination totalItems={filteredData.length} itemsPerPage={itemsPerPage} currentPage={currentPage} onPageChange={paginate} />
          )}
        </section>
      )}
</div>
  );
};

export default TransactionReports;